import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { baseUrl, getConfigWithToken } from '../../config/config';
import PayerMatrixPagination from './PayerMatrixPagination';
import { useSelector } from 'react-redux';

const InsurancesTab = ({ searchTerm = "", statusFilter = "" }) => {
    const navigate = useNavigate();
    const user = useSelector((state) => state?.user);
    const token = user?.token || localStorage.getItem("token") || localStorage.getItem("user_id");

    const [insurances, setInsurances] = useState([]);
    const [loading, setLoading] = useState(true);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchInsurances = async () => {
            setLoading(true);
            try {
                const config = getConfigWithToken(token);
                // Based on backend standard routes
                const res = await axios.get(`${baseUrl}insuranceProvider/all`, config);
                if (res.data && res.data.success) {
                    setInsurances(res.data.data || []);
                } else if (res.data && Array.isArray(res.data)) {
                    setInsurances(res.data);
                } else if (res.data && res.data.insuranceProviders) {
                    setInsurances(res.data.insuranceProviders);
                } else {
                    setInsurances([]);
                }
                setError(null);
            } catch (err) {
                console.error("Error fetching insurances:", err);
                setError("Failed to fetch insurances. Please try again.");
                setInsurances([]);
            } finally {
                setLoading(false);
            }
        };

        fetchInsurances();
    }, [token]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, statusFilter]);

    // Filter insurances based on searchTerm and statusFilter
    const filteredInsurances = useMemo(() => {
        return insurances.filter((item) => {
            const id = (item.insurance_id || item._id || item.id || "").toString().toLowerCase();
            const name = (item.insurance_name || item.insuranceName || item.name || "").toLowerCase();
            const payerId = (item.payer_id || item.payerId || "").toLowerCase();
            const status = (item.status || "active").toLowerCase();

            const search = searchTerm.toLowerCase();
            const matchesSearch = id.includes(search) || name.includes(search) || payerId.includes(search);

            const matchesStatus = !statusFilter || statusFilter === "All" || status === statusFilter.toLowerCase();

            return matchesSearch && matchesStatus;
        });
    }, [insurances, searchTerm, statusFilter]);

    const tableStyle = {
        width: '100%',
        borderCollapse: 'collapse',
    };

    const thStyle = {
        backgroundColor: '#064260',
        color: '#FFFFFF',
        padding: '14px 16px',
        textAlign: 'left',
        fontSize: '11px',
        fontWeight: '600',
        letterSpacing: '0.6px',
        textTransform: 'uppercase',
        fontFamily: 'Poppins, sans-serif',
        border: 'none',
        borderRadius: 0,
    };

    const tdStyle = {
        padding: '15px 20px',
        borderBottom: '1px solid #f1f3f4',
        fontSize: '14px',
        color: '#333333',
        backgroundColor: '#FFFFFF',
    };

    const getStatusStyle = (status) => {
        const isActive = (status || "active").toLowerCase() === 'active';
        return {
            padding: '6px 15px',
            borderRadius: '20px',
            fontSize: '13px',
            fontWeight: '600',
            display: 'inline-block',
            textAlign: 'center',
            minWidth: '80px',
            backgroundColor: isActive ? '#e6f7ec' : '#feecec',
            color: isActive ? '#28a745' : '#dc3545',
        };
    };

    const indexOfLastItem = currentPage * rowsPerPage;
    const indexOfFirstItem = indexOfLastItem - rowsPerPage;
    const currentItems = filteredInsurances.slice(indexOfFirstItem, indexOfLastItem);

    if (loading) {
        return <div style={{ textAlign: 'center', padding: '40px' }}>Loading insurances...</div>;
    }

    return (
        <div style={{ width: '100%', overflowX: 'auto', backgroundColor: '#FFFFFF' }}>
            {error ? (
                <div style={{ textAlign: 'center', padding: '40px', color: 'red' }}>{error}</div>
            ) : filteredInsurances.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666', fontSize: '16px' }}>No insurance data found</div>
            ) : (
                <>
                    <table className="admin-table mb-0" style={tableStyle}>
                        <thead>
                            <tr>
                                <th style={{ ...thStyle }}>ID</th>
                                <th style={thStyle}>Insurance Name</th>
                                <th style={thStyle}>Payer ID</th>
                                <th style={thStyle}>Status</th>
                                <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentItems.map((row, index) => {
                                // Model attributes: _id, insurance_id, insurance_name, payer_id, status
                                const displayId = row.insurance_id || (row._id ? row._id.substring(row._id.length - 4) : String(index + 1).padStart(4, '0'));
                                const displayName = row.insurance_name || row.name || 'Unknown Insurance';
                                const displayPayerId = row.payer_id || row.payerId || 'N/A';
                                const statusName = row.status ? (row.status.charAt(0).toUpperCase() + row.status.slice(1)) : 'Active';

                                return (
                                    <tr key={row._id || index}>
                                        <td style={tdStyle}>{displayId}</td>
                                        <td style={tdStyle}>{displayName}</td>
                                        <td style={tdStyle}>{displayPayerId}</td>
                                        <td style={tdStyle}>
                                            <span style={getStatusStyle(statusName)}>
                                                {statusName}
                                            </span>
                                        </td>
                                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                                            <button
                                                className="app-view-btn"
                                                onClick={() => navigate(`/payer-matrix/insurance/${row._id || row.insurance_id || row.id}`, { state: { insuranceData: row } })}
                                            >
                                                View
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    <div style={{ padding: '0 20px', borderTop: '1px solid #f1f3f4' }}>
                        <PayerMatrixPagination
                            totalItems={filteredInsurances.length}
                            rowsPerPage={rowsPerPage}
                            setRowsPerPage={setRowsPerPage}
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default InsurancesTab;
